import { defineQuiz } from "./_schema";
import type { Answers, PersonaKey, Question } from "./_schema";
import { deriveCortisolProfile } from "./cortisol-profile";
import { MM_0102 } from "./_experiments";

/**
 * CORTISOL QUIZ -- cold-traffic destination for the cortisol ad set
 * (the "8 Signs of High Cortisol in Men" static + the "you're not ugly,
 * you just have high cortisol" video).
 *
 * The quiz is the 8-signs ad unpacked: each ad panel = one question, in the
 * ad's exact panel order. Persuasion spine is MMLP047 (Upgrade Cortisol LP):
 * effort-vs-results empathy -> not-aging absolution -> cortisol-blocks-T
 * mechanism -> "drop your cortisol, free your testosterone". The verdict is
 * the locked T-suppression surface with cortisol-dominance forced Primary
 * (see quizzes/cortisol-profile.ts).
 *
 * 10 single-choice screens + 3 interstitials (2 bridges + analyzing) = 13.
 * Design spec: docs/specs/2026-06-05-cortisol-quiz-design.md.
 *
 * Like quiz-v6, this quiz has no GLP-1 screener, so `glp1-user` is not
 * reachable from the classifier.
 */

const QUESTION_ORDER = [
  "age",
  "puffy-face",
  "wake-pattern",
  "tension",
  "energy-3pm",
  "info-absolution",
  "body-composition",
  "sugar-cravings",
  "brain-fog",
  "libido",
  "when-started",
  "info-mechanism",
  "info-analyzing",
] as const;

const questions: Question[] = [
  {
    id: "age",
    type: "single",
    prompt: "How old are you?",
    options: [
      { value: "50-plus", label: "50+ years old" },
      { value: "40-49", label: "40–49 years old" },
      { value: "30-39", label: "30–39 years old" },
      { value: "20-29", label: "20–29 years old" },
    ],
  },
  {
    id: "puffy-face",
    type: "single",
    prompt: "When you look in the mirror, does your face look puffier or softer than it used to?",
    options: [
      { value: "yes-noticeably", label: "Yes, noticeably" },
      { value: "some-mornings", label: "Some mornings" },
      { value: "not-sure", label: "Not sure" },
      { value: "no", label: "No" },
    ],
  },
  {
    id: "wake-pattern",
    type: "single",
    prompt: "Do you wake up between 2 and 3 AM, for no reason?",
    options: [
      { value: "most-nights", label: "Most nights" },
      { value: "few-times-week", label: "A few times a week" },
      { value: "rarely", label: "Rarely" },
      { value: "never", label: "Never" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body: "Cortisol runs on a 24-hour rhythm. When it’s stuck high, it spikes in the early-morning hours. That’s the 2–3 AM wake-up.",
    },
  },
  {
    id: "tension",
    type: "single",
    prompt: "Do you carry physical tension like a tight neck, stiff shoulders, clenched jaw?",
    options: [
      { value: "constantly", label: "Constantly" },
      { value: "most-days", label: "By the end of most days" },
      { value: "occasionally", label: "Occasionally" },
      { value: "rarely", label: "Rarely" },
    ],
  },
  {
    id: "energy-3pm",
    type: "single",
    prompt: "How’s your energy at 3pm compared to when you wake up?",
    // Emojis dropped from the live-Q6 source question: every other sign
    // question here is plain, and one emoji screen mid-flow broke texture.
    // Option values stay byte-identical (deriver contract).
    options: [
      { value: "drained", label: "Completely drained, I need caffeine or a nap" },
      { value: "afternoon-slump", label: "Noticeably lower, afternoon slump hits hard" },
      { value: "slightly-tired", label: "Slightly tired, still functional but slower" },
      { value: "same-energy", label: "Same energy, no afternoon crash" },
    ],
  },
  {
    id: "info-absolution",
    type: "info",
    heading: "You’re not imagining it.",
    body: [
      "You go to the gym, eat clean, and yet the mirror still doesn’t match the effort.",
      "That’s not laziness, or a sign of “just getting older.” Modern life puts your body under a constant, low-grade pressure it was never built for. And if you don’t get this in check, your testosterone can take a serious hit.",
    ],
    // Renders below the Continue CTA (rule 20) as supporting proof. This is the
    // symptom-recognition bridge, so it carries the 8-signs grid (pays off the
    // intro's "8 signs of high cortisol" promise), NOT the mechanism diagram --
    // the mechanism diagram lives on info-mechanism below.
    image: {
      src: "/static/images/cortisol-signs.webp",
      alt: "Eight common signs of high cortisol: puffy face, waking at 2 to 3 AM, tensed neck, low energy, stubborn belly fat, sugar cravings, brain fog, low libido",
    },
  },
  {
    id: "body-composition",
    type: "single",
    prompt: "How would you describe your current body composition?",
    options: [
      { value: "lean-muscular", label: "Lean and muscular" },
      { value: "average", label: "Average build" },
      { value: "soft-some-muscle", label: "Soft with some muscle" },
      { value: "overweight", label: "Overweight / out of shape" },
    ],
  },
  {
    id: "sugar-cravings",
    type: "single",
    prompt: "Do sugar or junk-food cravings take over in the afternoon or evening?",
    options: [
      { value: "daily", label: "Daily" },
      { value: "few-times-week", label: "A few times a week" },
      { value: "occasionally", label: "Occasionally" },
      { value: "rarely", label: "Rarely" },
    ],
  },
  {
    id: "brain-fog",
    type: "single",
    prompt: "How often does brain fog hit — losing focus mid-task, walking into a room and forgetting why?",
    options: [
      { value: "daily", label: "Daily" },
      { value: "most-days", label: "Most days" },
      { value: "sometimes", label: "Sometimes" },
      { value: "rarely", label: "Rarely" },
    ],
  },
  {
    id: "libido",
    type: "single",
    prompt: "Has your drive dropped compared to a few years ago?",
    options: [
      { value: "significantly", label: "Significantly" },
      { value: "somewhat", label: "Somewhat" },
      { value: "not-sure", label: "Not sure" },
      { value: "no", label: "No" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body: "Testosterone and cortisol compete for real estate in your body. So when cortisol runs high, drive is usually the first thing to fade.",
    },
  },
  {
    id: "when-started",
    type: "single",
    prompt: "When did you start noticing these changes?",
    options: [
      { value: "over-year", label: "Over a year ago" },
      { value: "past-year", label: "In the past year" },
      { value: "few-months", label: "In the past few months" },
      { value: "not-sure", label: "Not sure" },
    ],
  },
  {
    id: "info-mechanism",
    type: "info",
    heading: "Your signs point to one thing: cortisol is suppressing your testosterone.",
    body: [
      "Cortisol is built for short bursts, but modern life never lets it switch off. Stuck high, it tells your body to store belly fat, hold water in your face, and stop making testosterone.",
      "Lower the cortisol, free the testosterone. Let’s see how yours is running.",
    ],
    // Testosterone is first named on the absolution bridge (info-absolution)
    // after the 2026-06-12 copywriter pass; this mechanism bridge is where the
    // full UMS lands.
    // The T-suppression mechanism diagram (SHBG locks T, estrogen conversion
    // drains it, less free T). Shared visual with the quiz-before-trt
    // info-blocked bridge -- same byte-identical diagram, kept as a per-quiz
    // copy so each quiz's assets stay self-contained.
    image: {
      src: "/static/images/cortisol-mechanism.webp",
      alt: "How testosterone gets suppressed: the brain signals the testes, but SHBG locks up testosterone and estrogen conversion drains it, leaving less free testosterone in the body",
    },
  },
  {
    id: "info-analyzing",
    type: "info",
    // Pays off Bridge 2's closing line ("Let's see how yours is running").
    heading: "Analyzing your cortisol profile…",
    body: [
      "Counting the signs you just reported, and measuring how hard your cortisol is working against your testosterone.",
    ],
    animation: "analyzing",
    autoAdvanceMs: 5000,
  },
];

export const cortisolQuiz = defineQuiz({
  id: "cortisol",
  slug: "cortisol",
  // MM-0093 ("Swap Quiz Q1 From Age to the Angle Question") concluded
  // 2026-09-01 with the promoted-angle arm LOSING on every high-volume quiz
  // (n~47k/arm); the age-first control order is what ships, and its
  // `variantMoves` blocks were removed 2026-09-02. Do not revive it.
  //
  // MM-0102 is what is armed now, and it changes no screen order at all --
  // only whether screen one renders its intro block. See
  // quizzes/_experiments.ts.
  experiment: MM_0102,
  meta: {
    title: "What’s Really Driving Your Cortisol",
    introHeadline:
      "Is high cortisol behind why you’re feeling softer, puffier, and more tired than you should be?",
    introAccent: "behind",
    introBody: "There are 8 signs of high cortisol. See how many you have in this 2 minute quiz.",
    testimonial: {
      // Quiz-benefit entry proof (replaces the weight-loss/result register):
      // reveal-safe, cortisol-flavored, names no product or number. Operator to
      // back the Verified tag with a real review before relying on it.
      quote:
        "The quiz took two minutes and finally explained why I’m wired all day but exhausted by 3pm. Wish I’d taken it sooner.",
      author: "MARK, Verified Review",
      avatar: "/static/images/cortisol-testimonial-avatar.webp",
    },
    // MM-0102 -- see quizzes/_experiments.ts.
    introVariantTag: "control",
  },
  questions,

  branching(_answers: Answers, currentQuestionId: string): string | null {
    const idx = QUESTION_ORDER.indexOf(currentQuestionId as (typeof QUESTION_ORDER)[number]);
    if (idx < 0) return null;
    const next = QUESTION_ORDER[idx + 1];
    return next ?? null;
  },

  classifier(answers: Answers): PersonaKey {
    const age = answers["age"];
    const body = answers["body-composition"];
    const energy = answers["energy-3pm"];
    const onset = answers["when-started"];
    const libido = answers["libido"];
    const fog = answers["brain-fog"];

    // Note: puffy-face, wake-pattern, tension, and sugar-cravings are not read
    // here -- they are persuasion / severity screens, not routing signals (they
    // feed the deriver via quizzes/cortisol-profile.ts). The classifier routes
    // on age, body-composition, energy, onset, libido, and brain-fog only.

    // Performance-focused: lean body chasing a cognitive/energy edge. Runs
    // first -- lean-muscular users never reach trt-alternative even with a
    // long-running collapse. Intentional: body composition signals audience.
    if (body === "lean-muscular" && (fog === "daily" || fog === "most-days" || energy === "drained")) {
      return "performance";
    }

    // TRT-alternative: long-running collapse across energy + drive.
    if (onset === "over-year" && energy === "drained" && (libido === "significantly" || age === "50-plus")) {
      return "trt-alternative";
    }

    // Skeptical buyer: can't place when it started.
    if (onset === "not-sure") {
      return "skeptical-buyer";
    }

    // Aging men: 40+ with visible softening.
    if ((age === "50-plus" || age === "40-49") && (body === "soft-some-muscle" || body === "overweight")) {
      return "aging-men";
    }

    return "everyday-man";
  },

  deriveProfile: deriveCortisolProfile,
});
